import {
  getTaskTemplateOptions, listTaskTemplates, type TaskTemplateListType,
} from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { translate, TranslationEntity } from '@repo/utils';
import {
  Checkbox,
  Col,
  Form, Input, Row, Select,
} from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { type CreateTaskNodeData } from 'automations/types/nodeSchemas/actionNodes';
import {
  forwardRef, useEffect, useImperativeHandle, useMemo,
} from 'react';

import AddDescriptionForm from './AddDescriptionForm';
import AddSubjectForm from './AddSubjectForm';
import styles from './CreateTaskContent.module.scss';
import FormItemTitle from './FormItemTitle';
import ModifyReminderForm from './ModifyReminderForm';
import ModifyScheduleForm from './ModifyScheduleForm';

type CreateTaskContentType = CreateTaskNodeData['additionalOptions']['createTask'];

const ConstantValidatorRule = (input: string, expectedValue: string, name: string): any => {
  if (input !== expectedValue) {
    return Promise.reject(new Error(`${name} can only be equal to \`${expectedValue}\``));
  }
  return Promise.resolve();
};

export interface ModifyTaskPayloadFormRef {
  submit: () => void;
  validate: () => Promise<any>;
}

function ModifyTaskPayloadForm({
  taskData,
  setTaskData,
}: {
  taskData: Partial<CreateTaskContentType>;
  setTaskData: (data: Partial<CreateTaskContentType>) => void;
}, ref: React.ForwardedRef<ModifyTaskPayloadFormRef>) {
  const [form] = useForm<CreateTaskContentType>();
  const formValues = useWatch([], form);

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    validate: () => form.validateFields(),
  }));

  const { data: templateData } = useGetGraphqlDataQuery<UseQueryResult<TaskTemplateListType>>({
    document: listTaskTemplates,
  });

  useEffect(() => form.setFieldsValue(taskData), [form, taskData]);
  useEffect(() => setTaskData(formValues), [formValues, setTaskData]);

  const taskTemplateOptions = useMemo(() => (
    templateData ? getTaskTemplateOptions(templateData) : []
  ), [templateData]);

  return (
    <Form
      form={form}
      layout="horizontal"
      colon={false}
      requiredMark={false}
    >
      <Form.Item
        name="templateId"
        label={<FormItemTitle>Task Template</FormItemTitle>}
        rules={[{ required: true }]}
      >
        <Select options={taskTemplateOptions} />
      </Form.Item>
      <Row className={styles.row}>
        <Col span={9}>
          <Form.Item
            name="owner"
            label={<FormItemTitle>Owner</FormItemTitle>}
            rules={[
              { required: true },
              { validator: (_, value) => ConstantValidatorRule(value, 'lead.owner', 'Owner') },
            ]}
          >
            <Input placeholder={translate(`Enter the Owner of the #{${TranslationEntity.task}}`)} />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item name={['config', 'calendarInviteForOwner']} noStyle valuePropName="checked">
            <Checkbox>Calendar Invite</Checkbox>
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.row}>
        <Col span={9}>
          <Form.Item
            name="associateWith"
            label={<FormItemTitle>Associate With</FormItemTitle>}
            rules={[{ validator: (_, value) => ConstantValidatorRule(value, 'lead', 'Associate With') }]}
          >
            <Input placeholder={translate(`Enter the Associate With of the #{${TranslationEntity.task}}`)} />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item name={['config', 'calendarInviteForLead']} noStyle valuePropName="checked">
            <Checkbox>Calendar Invite</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <AddSubjectForm />
      <AddDescriptionForm />

      <Form.Item name="organizer" label={<FormItemTitle>Organizer</FormItemTitle>} hidden>
        <Input />
      </Form.Item>
      <Form.Item label={<FormItemTitle>Reminder</FormItemTitle>}>
        <ModifyReminderForm taskTemplateData={templateData} />
      </Form.Item>
      <Form.Item label={<FormItemTitle>Schedule</FormItemTitle>}>
        <ModifyScheduleForm taskTemplateData={templateData} />
      </Form.Item>
    </Form>
  );
}

export default forwardRef(ModifyTaskPayloadForm);
